import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import api from "../../../utils/api";
import {
  animations,
  styles,
  Hero,
  SectionHeader,
  CTASection,
} from "../../../components/landing/SharedComponent";
import ProductDetail from "./ProductDetail";

const Product = () => {
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get("/api/products");
        const allProducts = response.data;

        // Filter featured products
        const featured = allProducts.filter((product) => product.is_featured);

        setProducts(allProducts);
        setFeaturedProducts(featured);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const materials = [
    {
      id: 1,
      name: "LiFePO4 (Phosphate) Powder",
      subtitle: "for Li-ion Battery Cathode",
      image: "images/landing/product_page/kerikil-1.jpg",
      category: "Battery Materials",
      detail: "detail-material",
    },
    {
      id: 2,
      name: "LiPO2(Manganese) Powder",
      subtitle: "for Li-Ion Battery Cathode",
      image: "images/landing/product_page/kerikil-2.jpg",
      category: "Battery Materials",
      detail: "detail-material",
    },
    {
      id: 3,
      name: "Li(NiCoMn)O2 NCPO",
      subtitle: "for High Capacity Li-Ion Battery Cathode",
      image: "images/landing/product_page/kerikil-3.jpg",
      category: "Battery Materials",
      detail: "detail-material",
    },
    {
      id: 4,
      name: "Lithium Nickel Cobalt Aluminum Oxide",
      subtitle: "NCA Battery Material",
      image: "images/landing/product_page/kerikil-4.jpg",
      category: "Battery Materials",
      detail: "detail-material",
    },
    {
      id: 5,
      name: "LiNiCoO2 6V-85C",
      subtitle: "with high performance",
      image: "images/landing/product_page/kerikil-5.jpg",
      category: "High Performance Materials",
      detail: "detail-material",
    },
    {
      id: 6,
      name: "Electrode for super capacitor",
      subtitle: "High Performance Electrode",
      image: "images/landing/product_page/kerikil-6.jpg",
      category: "Components",
      detail: "detail-material",
    },
    {
      id: 7,
      name: "Lithium cobalt oxide 4.45V high voltage",
      subtitle: "Advanced Battery Material",
      image: "images/landing/product_page/kerikil-7.png",
      category: "Battery Materials",
      detail: "detail-material",
    },
    {
      id: 8,
      name: "LFP coated Al foil",
      subtitle: "Special Coating",
      image: "images/landing/product_page/kerikil-8.jpg",
      category: "Coating",
      detail: "detail-material",
    },
  ];

  // Get unique categories for filter
  const categories = [
    "All",
    ...new Set(materials.map((item) => item.category)),
  ];

  const defaultFeatures = [
    "High Quality",
    "Premium Material",
    "Industry Standard",
    "Professional Grade",
  ];

  const formatProduct = (product, index) => {
    // Safety check to ensure product exists
    if (!product) return null;

    return {
      id: product.id || index,
      title: product.title || `Product ${index + 1}`,
      description: product.description || "Description not available",
      image: product.image_path
        ? `${process.env.REACT_APP_API_URL}${product.image_path}`
        : `images/landing/product_page/product-${(index % 4) + 1}.png`,
      features: (product.features || defaultFeatures).map((feature) =>
        typeof feature === "string" ? { feature_name: feature } : feature
      ),
      specifications: product.specifications || [],
      certifications: product.certifications || [],
      testingCapabilities: product.testingCapabilities || [],
      validationMethods: product.validationMethods || [],
      category: product.category || "Battery Materials",
      detail: `/product/detail/${product.id || index}`,
    };
  };

  // Use ONLY featured products from API
  const displayProducts = featuredProducts;

  const ProductCard = ({ product, isLarge = false, index }) => {
    // Safety check - if product is null, don't render anything
    if (!product) return null;

    return (
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true, amount: 0.3 }}
        custom={index}
        variants={animations.fadeInUp}
        transition={{ delay: index * 0.1 }}
        className={`bg-white rounded-lg ${styles.shadowLarge} overflow-hidden group h-full cursor-pointer`}
        onClick={() => {
          setCurrentProduct(product);
          setIsModalOpen(true);
        }}
      >
        <div className="relative h-full flex flex-col">
          <div className={isLarge ? "h-72" : "h-52"}>
            <motion.img
              src={product.image}
              alt={product.title}
              className={`w-full h-full object-cover ${styles.transitionBase} group-hover:scale-105`}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/400x300?text=No+Image";
              }}
            />
            {/* Image overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
              <div className="text-white text-sm font-medium mb-2">
                View Details
              </div>
            </div>
          </div>
          <div className="p-4 flex-grow flex flex-col">
            <div className="mb-2">
              {/* <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
                {product.category}
              </span> */}
            </div>
            <h2
              className={`${
                isLarge ? "text-xl" : "text-lg"
              } font-bold mb-2 text-gray-800 line-clamp-2`}
            >
              {product.title}
            </h2>
            <p className="text-gray-600 text-sm mb-3 line-clamp-2">
              {product.description}
            </p>

            {/* Features list */}
            <div className="mt-auto">
              <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                {product.features
                  .slice(0, isLarge ? 4 : 2)
                  .map((feature, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-1 text-xs text-gray-600"
                    >
                      <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                      <span>{feature.feature_name || feature}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const MaterialCard = ({ material, index }) => (
    <motion.div
      variants={animations.fadeInUp}
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, amount: 0.3 }}
      custom={index}
      transition={{ delay: index * 0.1 }}
      className={`bg-white rounded-lg ${styles.shadowBase} overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1`}
    >
      <Link to={material.detail} className="flex flex-col h-full">
        <div className="h-48 overflow-hidden relative">
          <motion.img
            src={material.image}
            alt={material.name}
            className={`w-full h-full object-cover ${styles.transitionBase}`}
            whileHover={{ scale: 1.05 }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end justify-start p-4">
            <span className="text-white text-sm font-medium">View Details</span>
          </div>
        </div>
        <div className="p-4 flex flex-col flex-grow">
          <div className="mb-2">
            <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
              {material.category}
            </span>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1 line-clamp-2">
            {material.name}
          </h3>
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
            {material.subtitle}
          </p>
          <div className="mt-auto pt-2">
            <span className="text-sm font-medium text-blue-600 flex items-center">
              View Details
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 ml-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );

  // Filter materials by category
  const filteredMaterials =
    activeCategory === "All"
      ? materials
      : materials.filter((material) => material.category === activeCategory);

  return (
    <div className="min-h-screen bg-gray-50">
      <Hero
        title="Our Products"
        subtitle="Advanced Materials and Equipment for Industrial Solutions"
        backgroundImage="/images/landing/bg.png"
      />

      {/* Featured Products Section */}
      <div className={`${styles.container} ${styles.sectionSpacing}`}>
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <motion.div
              className="flex items-center text-primary"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 mr-2 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Loading products...
            </motion.div>
          </div>
        ) : (
          <>
            <motion.div
              className="mb-10"
              variants={animations.fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              <SectionHeader
                title="Featured Products"
                subtitle="Our most popular and high-quality products"
              />
            </motion.div>

            {!displayProducts || displayProducts.length === 0 ? (
              <div className="flex justify-center items-center py-16">
                <motion.div
                  className="text-center"
                  variants={animations.fadeInUp}
                  initial="initial"
                  whileInView="animate"
                  viewport={{ once: true }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-16 w-16 mx-auto text-gray-400 mb-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                  <h3 className="text-xl font-medium text-gray-700 mb-2">
                    No featured products
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    Currently there are no featured products available. Please
                    check back later or view all our products.
                  </p>
                </motion.div>
              </div>
            ) : (
              <>
                {/* New layout for featured products */}
                <motion.div
                  className="grid grid-cols-1 lg:grid-cols-3 gap-6"
                  variants={animations.staggerChildren}
                  initial="initial"
                  whileInView="animate"
                  viewport={{ once: true }}
                >
                  {/* First featured product (larger) */}
                  {displayProducts[0] && (
                    <div className="lg:col-span-2 h-full">
                      <ProductCard
                        product={formatProduct(displayProducts[0], 0)}
                        isLarge={true}
                        index={0}
                      />
                    </div>
                  )}

                  {/* Second featured product */}
                  {displayProducts[1] && (
                    <div className="h-full">
                      <ProductCard
                        product={formatProduct(displayProducts[1], 1)}
                        index={1}
                      />
                    </div>
                  )}
                </motion.div>

                {/* Second row of featured products */}
                {(displayProducts[2] || displayProducts[3]) && (
                  <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6"
                    variants={animations.staggerChildren}
                    initial="initial"
                    whileInView="animate"
                    viewport={{ once: true }}
                  >
                    {/* Two more featured products */}
                    {displayProducts.slice(2, 4).map(
                      (product, index) =>
                        product && (
                          <div
                            key={product.id || `product-${index}`}
                            className="h-full"
                          >
                            <ProductCard
                              product={formatProduct(product, index + 2)}
                              index={index + 2}
                            />
                          </div>
                        )
                    )}
                  </motion.div>
                )}
              </>
            )}

            {/* View All Products Button */}
            <motion.div
              className="flex justify-center mt-12"
              variants={animations.fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              <Link
                to="/product/all-product"
                className={`${styles.button} px-6 py-3 rounded text-white flex items-center gap-2 hover:scale-105 transition-transform duration-300`}
              >
                <span>View All Products</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </Link>
            </motion.div>
          </>
        )}
      </div>

      {/* Materials Section */}
      <div className="bg-white py-16">
        <div className={styles.container}>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={animations.fadeInUp}
          >
            <SectionHeader
              title="Lithium Battery Cathode Materials"
              subtitle="Our company provides raw materials for battery cathodes and mineral needs for battery materials (energy storage) that can be supplied domestically"
            />
          </motion.div>

          {/* Category Filter */}
          <motion.div
            className="flex flex-wrap gap-2 mb-8 justify-center"
            variants={animations.fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {categories.map((category) => (
              <button
                key={category}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === category
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                }`}
                onClick={() => setActiveCategory(category)}
              >
                {category === "All" ? "All" : category}
              </button>
            ))}
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={animations.staggerChildren}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {filteredMaterials.map((material, index) => (
              <MaterialCard
                key={material.id}
                material={material}
                index={index}
              />
            ))}
          </motion.div>

          {filteredMaterials.length === 0 && (
            <div className="flex justify-center items-center py-10">
              <motion.div
                className="text-center"
                variants={animations.fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 mx-auto text-gray-400 mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
                <p className="text-gray-600">
                  No products available in this category
                </p>
              </motion.div>
            </div>
          )}
        </div>
      </div>

      <CTASection
        title="Interested in Our Products?"
        subtitle="Contact us to learn more about our products and materials"
        buttonText="Contact Us"
        buttonLink="/contact"
      />

      <ProductDetail
        product={
          currentProduct
            ? products.find((p) => p.id === currentProduct.id)
            : null
        }
        products={products}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onProductSwitch={(product) =>
          setCurrentProduct(formatProduct(product, products.indexOf(product)))
        }
      />
    </div>
  );
};

export default Product;
